/**
 * Routes Configuration
 * @apal/web-humanda
 *
 * Routing for the standalone HUMANDA portal application.
 * Routes are at root level (no /humanda prefix).
 */

import * as React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

import { AuthGuard, GuestGuard } from "@apal/shared-auth";
import { ErrorBoundary } from "@apal/shared-ui";
import { TierGuard } from "./guards/tier-guard";
import { RoleGuard } from "./guards/role-guard";
import { AccessDenied } from "./components/access-denied";
import { paths } from "./config/paths";
import { defaultSettings } from "./config/dashboard";
import { SettingsProvider } from "./components/core/settings/settings-context";
import { VerticalLayout } from "./components/dashboard/layout/vertical/vertical-layout";

// Lazy-loaded pages
const LoginPage = React.lazy(() => import("./pages/login"));
const RegisterPage = React.lazy(() => import("./pages/register"));
const ForgotPasswordPage = React.lazy(() => import("./pages/forgot-password"));
const ResetPasswordPage = React.lazy(() => import("./pages/reset-password"));
const DashboardIndexPage = React.lazy(() => import("./pages/dashboard/index"));
// REQ-BVDR-FE-1: pathless BVDR buyer attestation gate (wraps all dashboard children)
const AttestationGate = React.lazy(() =>
  import("./components/attestation/attestation-gate").then((m) => ({
    default: m.AttestationGate,
  }))
);
const HchsDashboardPage = React.lazy(() => import("./pages/dashboard/hchs/index"));
const HchsMetricsPage = React.lazy(() => import("./pages/dashboard/hchs/metrics"));
const ListingsListPage = React.lazy(() => import("./pages/dashboard/listings/index"));
const ListingsCreatePage = React.lazy(() => import("./pages/dashboard/listings/create"));
const ListingsDetailPage = React.lazy(() => import("./pages/dashboard/listings/[id]"));
const ListingsEditPage = React.lazy(() => import("./pages/dashboard/listings/[id]/edit"));
const ListingsPreviewPage = React.lazy(() => import("./pages/dashboard/listings/[id]/preview"));
const BrokerDashboardPage = React.lazy(() => import("./pages/dashboard/broker/index"));
const BrokerClientsPage = React.lazy(() => import("./pages/dashboard/broker/clients/index"));
const BrokerClientDetailPage = React.lazy(() => import("./pages/dashboard/broker/clients/[clientId]"));
// REQ-BVDR-FE-5
const BrokerCommissionsPage = React.lazy(() => import("./pages/dashboard/broker/commissions/index"));
const BrokerOutreachPage = React.lazy(() => import("./pages/dashboard/broker/outreach/index"));
const MarketplaceSearchPage = React.lazy(() => import("./pages/dashboard/marketplace/index"));
const MarketplaceDetailPage = React.lazy(() => import("./pages/dashboard/marketplace/[id]"));
// REQ-BVDR-FE-7: retired legacy purchase flow. The /purchase sub-route now
// redirects to the marketplace detail (Enter BVDR). Legacy buyer-history and
// Stripe callback routes redirect to /dashboard/rooms (My BVDRs).
const MarketplacePurchasePage = React.lazy(() => import("./pages/dashboard/marketplace/[id]/purchase"));
const BuyersListPage = React.lazy(() => import("./pages/dashboard/buyers/index"));
// REQ-BVDR-FE-2: buyer "My BVDRs" / access-request list (append-only)
const BuyerRoomsPage = React.lazy(() => import("./pages/dashboard/rooms/index"));
// REQ-BVDR-FE-3: seller approval console (append-only)
const SellerRequestsPage = React.lazy(() => import("./pages/dashboard/seller-requests/index"));
const ReportsHistoryPage = React.lazy(() => import("./pages/dashboard/reports/history"));
const ReportsGeneratePage = React.lazy(() => import("./pages/dashboard/reports/generate"));
const ReportViewPage = React.lazy(() => import("./pages/dashboard/reports/[id]"));
const AnalyticsPage = React.lazy(() => import("./pages/dashboard/analytics/index"));
const AdminAccessLogsPage = React.lazy(() => import("./pages/admin/access-logs/index"));
const AdminForensicLookupPage = React.lazy(() => import("./pages/admin/forensics/index"));
const NotFoundPage = React.lazy(() => import("./pages/not-found"));
const NotAuthorizedPage = React.lazy(() => import("./pages/errors/not-authorized"));
const AccountIndexPage = React.lazy(() => import("./pages/dashboard/account/index"));
const AccountProfilePage = React.lazy(() => import("./pages/dashboard/account/profile"));
const AccountSecurityPage = React.lazy(() => import("./pages/dashboard/account/security"));
const AccountNotificationsPage = React.lazy(() => import("./pages/dashboard/account/notifications"));
const AccountSessionsPage = React.lazy(() => import("./pages/dashboard/account/sessions"));
const BillingPage = React.lazy(() => import("./pages/dashboard/billing/index"));
const BillingPlansPage = React.lazy(() => import("./pages/dashboard/billing/plans"));
const SubscriptionSuccessPage = React.lazy(() => import("./pages/checkout/subscription/success"));
const SubscriptionCancelPage = React.lazy(() => import("./pages/checkout/subscription/cancel"));

// Help pages (REQ-043: Help Articles Portal)
const HelpIndexPage = React.lazy(() => import("./pages/dashboard/help/index"));
const HelpArticlePage = React.lazy(() => import("./pages/dashboard/help/article"));
const HelpContactPage = React.lazy(() => import("./pages/dashboard/help/contact"));

// Certification / LMS pages (REQ-BVDR-FE-6) — APPEND-ONLY
const CertificationsCatalogPage = React.lazy(() => import("./pages/dashboard/certifications/index"));
const CertificationDetailPage = React.lazy(() => import("./pages/dashboard/certifications/[slug]"));
const AdminCertificationsPage = React.lazy(() => import("./pages/dashboard/admin/certifications/index"));

// Loading fallback
function LoadingFallback() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      Loading...
    </div>
  );
}

// Dashboard layout wrapper with auth protection and sidebar navigation
function DashboardLayout() {
  const navigate = useNavigate();

  const handleUnauthenticated = React.useCallback(() => {
    navigate(paths.login, { replace: true });
  }, [navigate]);

  const handleUnauthorized = React.useCallback(() => {
    navigate(paths.errors.notAuthorized, { replace: true });
  }, [navigate]);

  return (
    <AuthGuard
      currentPortal="HUMANDA"
      onUnauthenticated={handleUnauthenticated}
      onUnauthorized={handleUnauthorized}
      loadingComponent={<LoadingFallback />}
    >
      <SettingsProvider settings={defaultSettings}>
        <VerticalLayout>
          <ErrorBoundary sectionName="Dashboard">
            <React.Suspense fallback={<LoadingFallback />}>
              <Outlet />
            </React.Suspense>
          </ErrorBoundary>
        </VerticalLayout>
      </SettingsProvider>
    </AuthGuard>
  );
}

// Guest layout wrapper (for login/register)
function GuestLayout() {
  const navigate = useNavigate();

  const handleAuthenticated = React.useCallback(() => {
    navigate(paths.dashboard.index, { replace: true });
  }, [navigate]);

  return (
    <GuestGuard
      onAuthenticated={handleAuthenticated}
      loadingComponent={<LoadingFallback />}
    >
      <React.Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </React.Suspense>
    </GuestGuard>
  );
}

export const routes: RouteObject[] = [
  // Root redirect to login
  {
    index: true,
    element: <Navigate to={paths.login} replace />,
  },

  // Guest routes (login, register, etc.)
  {
    element: <GuestLayout />,
    children: [
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "register",
        element: <RegisterPage />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Dashboard routes (protected)
  {
    path: "dashboard",
    element: <DashboardLayout />,
    // REQ-BVDR-FE-1: pathless AttestationGate wrapper sits BETWEEN DashboardLayout
    // (AuthGuard) and the dashboard children. It blocks ALL /dashboard/* routes
    // until the server confirms needs_attestation=false. No new URL path is added
    // and no config/nav.ts entry is required — it is purely a route-tree guard.
    children: [
      {
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <AttestationGate />
          </React.Suspense>
        ),
        children: [
      {
        index: true,
        element: <DashboardIndexPage />,
      },
      {
        path: "hchs",
        children: [
          {
            index: true,
            element: <HchsDashboardPage />,
          },
          {
            path: "metrics",
            element: <HchsMetricsPage />,
          },
        ],
      },
      {
        path: "reports",
        children: [
          {
            path: "history",
            element: <ReportsHistoryPage />,
          },
          {
            path: "generate",
            element: <ReportsGeneratePage />,
          },
          {
            path: ":id",
            element: <ReportViewPage />,
          },
        ],
      },
      // Listings (seller) - Requires T3+ tier (sellers, executives, admins)
      {
        path: "listings",
        children: [
          {
            index: true,
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Seller or higher access is required to manage listings." />}>
                <ListingsListPage />
              </TierGuard>
            ),
          },
          {
            path: "create",
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Seller or higher access is required to create listings." />}>
                <ListingsCreatePage />
              </TierGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Seller or higher access is required to view listing details." />}>
                <ListingsDetailPage />
              </TierGuard>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Seller or higher access is required to edit listings." />}>
                <ListingsEditPage />
              </TierGuard>
            ),
          },
          {
            path: ":id/preview",
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Seller or higher access is required to preview listings." />}>
                <ListingsPreviewPage />
              </TierGuard>
            ),
          },
        ],
      },
      // Broker Dashboard - Requires broker, executive, or admin role
      {
        path: "broker",
        children: [
          {
            index: true,
            element: (
              <RoleGuard allowedRoles={["broker", "executive", "admin"]}>
                <BrokerDashboardPage />
              </RoleGuard>
            ),
          },
          {
            path: "clients",
            children: [
              {
                index: true,
                element: (
                  <RoleGuard allowedRoles={["broker", "executive", "admin"]}>
                    <BrokerClientsPage />
                  </RoleGuard>
                ),
              },
              {
                path: ":clientId",
                element: (
                  <RoleGuard allowedRoles={["broker", "executive", "admin"]}>
                    <BrokerClientDetailPage />
                  </RoleGuard>
                ),
              },
            ],
          },
          // REQ-BVDR-FE-5 — Commissions (read-only ledger)
          {
            path: "commissions",
            element: (
              <RoleGuard allowedRoles={["broker", "executive", "admin"]}>
                <BrokerCommissionsPage />
              </RoleGuard>
            ),
          },
          // REQ-BVDR-FE-5 — Outreach
          {
            path: "outreach",
            element: (
              <RoleGuard allowedRoles={["broker", "executive", "admin"]}>
                <BrokerOutreachPage />
              </RoleGuard>
            ),
          },
        ],
      },
      // Marketplace (buyer search) - Requires buyer, executive, or admin role
      {
        path: "marketplace",
        children: [
          {
            index: true,
            element: (
              <RoleGuard allowedRoles={["buyer", "executive", "admin"]}>
                <MarketplaceSearchPage />
              </RoleGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <RoleGuard allowedRoles={["buyer", "executive", "admin"]}>
                <MarketplaceDetailPage />
              </RoleGuard>
            ),
          },
          {
            path: ":id/purchase",
            element: (
              <RoleGuard allowedRoles={["buyer", "executive", "admin"]}>
                <MarketplacePurchasePage />
              </RoleGuard>
            ),
          },
        ],
      },
      // REQ-BVDR-FE-7: legacy buyer purchase history retired. Redirect old
      // bookmarks/deep links to the live "My BVDRs" aggregate (rooms).
      {
        path: "purchases",
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/rooms" replace />,
          },
          {
            path: ":purchaseId",
            element: <Navigate to="/dashboard/rooms" replace />,
          },
        ],
      },
      // REQ-BVDR-FE-2: buyer "My BVDRs" / access-request list (append-only)
      {
        path: "rooms",
        element: (
          <RoleGuard allowedRoles={["buyer", "executive", "admin"]}>
            <BuyerRoomsPage />
          </RoleGuard>
        ),
      },
      // REQ-BVDR-FE-3: seller approval console (append-only)
      {
        path: "seller-requests",
        element: (
          <RoleGuard allowedRoles={["seller", "executive", "admin"]}>
            <SellerRequestsPage />
          </RoleGuard>
        ),
      },
      {
        path: "buyers",
        children: [
          {
            index: true,
            element: <BuyersListPage />,
          },
        ],
      },
      {
        path: "analytics",
        element: (
          <RoleGuard allowedRoles={["admin", "executive"]}>
            <AnalyticsPage />
          </RoleGuard>
        ),
      },
      // Help Center (REQ-043: Help Articles Portal)
      {
        path: "help",
        children: [
          {
            index: true,
            element: <HelpIndexPage />,
          },
          {
            path: "contact",
            element: <HelpContactPage />,
          },
          {
            path: ":slug",
            element: <HelpArticlePage />,
          },
        ],
      },
      // Account Settings (REQ-029 Phase 2)
      {
        path: "account",
        children: [
          {
            index: true,
            element: <AccountIndexPage />,
          },
          {
            path: "profile",
            element: <AccountProfilePage />,
          },
          {
            path: "security",
            element: <AccountSecurityPage />,
          },
          {
            path: "notifications",
            element: <AccountNotificationsPage />,
          },
          {
            path: "sessions",
            element: <AccountSessionsPage />,
          },
        ],
      },
      // Billing & Subscription (REQ-039) — Requires seller, broker, executive, or admin role
      {
        path: "billing",
        children: [
          {
            index: true,
            element: (
              <RoleGuard allowedRoles={["seller", "broker", "executive", "admin"]}>
                <BillingPage />
              </RoleGuard>
            ),
          },
          {
            path: "plans",
            element: (
              <RoleGuard allowedRoles={["seller", "broker", "executive", "admin"]}>
                <BillingPlansPage />
              </RoleGuard>
            ),
          },
        ],
      },
      // Admin pages (REQ-017 Phase 4 - Anti-Copy Protections) - Requires T5 (Admin only)
      {
        path: "admin",
        children: [
          {
            path: "access-logs",
            element: (
              <TierGuard minTier={5} fallback={<AccessDenied message="Administrator access is required to view access logs." />}>
                <AdminAccessLogsPage />
              </TierGuard>
            ),
          },
          {
            path: "forensics",
            element: (
              <TierGuard minTier={5} fallback={<AccessDenied message="Administrator access is required to use forensic lookup." />}>
                <AdminForensicLookupPage />
              </TierGuard>
            ),
          },
          // Admin Certifications (REQ-BVDR-FE-6) — APPEND-ONLY, T5 (Admin only)
          {
            path: "certifications",
            element: (
              <TierGuard minTier={5} fallback={<AccessDenied message="Administrator access is required to manage certifications." />}>
                <AdminCertificationsPage />
              </TierGuard>
            ),
          },
        ],
      },
      // Certifications (REQ-BVDR-FE-6) — APPEND-ONLY learner routes.
      // Informational; no special guard beyond DashboardLayout auth.
      {
        path: "certifications",
        children: [
          {
            index: true,
            element: <CertificationsCatalogPage />,
          },
          {
            path: ":slug",
            element: <CertificationDetailPage />,
          },
        ],
      },
        ], // REQ-BVDR-FE-1: end AttestationGate wrapper children
      }, // REQ-BVDR-FE-1: end pathless AttestationGate wrapper route
    ],
  },

  // REQ-BVDR-FE-7: dead Stripe purchase callback routes retired. No buy-flow
  // issues these redirects anymore; any stale hit lands on "My BVDRs" (rooms).
  {
    path: "purchases",
    children: [
      {
        path: ":purchaseId/success",
        element: <Navigate to="/dashboard/rooms" replace />,
      },
      {
        path: ":purchaseId/cancel",
        element: <Navigate to="/dashboard/rooms" replace />,
      },
    ],
  },

  // Subscription checkout callback routes (outside DashboardLayout - Stripe redirects directly here)
  {
    path: "checkout/subscription",
    children: [
      {
        path: "success",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <SubscriptionSuccessPage />
          </React.Suspense>
        ),
      },
      {
        path: "cancel",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <SubscriptionCancelPage />
          </React.Suspense>
        ),
      },
    ],
  },

  // Error routes
  {
    path: "errors",
    children: [
      {
        path: "not-authorized",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <NotAuthorizedPage />
          </React.Suspense>
        ),
      },
      {
        path: "not-found",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <NotFoundPage />
          </React.Suspense>
        ),
      },
    ],
  },

  // 404 catch-all
  {
    path: "*",
    element: (
      <React.Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </React.Suspense>
    ),
  },
];
